import { useRef } from 'react';
import { ArrowStrip } from '../../shared/arrow_strip.js';

export type HotelDetailsTab = { id: string; label: string };

/** O recorte da copy do painel que a régua lê — estrutural, não um `Pick` do
 *  tipo do painel, porque o painel importa esta régua. */
type HotelDetailsTabsCopy = {
  readonly sectionsNavigationLabel: string;
  readonly previousTabsLabel: string;
  readonly nextTabsLabel: string;
};

/**
 * A régua de seções do detalhe do hotel: fica pregada no topo do corpo que
 * rola (a `-mx-6` desfaz o respiro lateral do corpo pra barra sangrar até as
 * bordas), e a lista de abas rola na horizontal dentro da `ArrowStrip` — a
 * mesma faixa com setas e esmaecimento dos filtros de quarto (o
 * `FaixaComSetas` do design hub). Antes havia um `›` próprio, plantado por
 * cima da última aba sem máscara: o texto aparecia dos dois lados do botão.
 *
 * `px-3` na barra e `px-3` na lista: a primeira aba continua a 1.5rem da borda
 * (o respiro do corpo) e as setas da faixa ficam 0.75rem pra dentro, em vez
 * de coladas na borda da tela ou no canto arredondado da gaveta. No toque não
 * há setas (ver `ArrowStrip`): o respiro todo passa pra lista (`px-6`) e ela
 * corta nas bordas da barra, a aba cortada dizendo que tem mais (#6203).
 *
 * O `before:` é o cobertor de 0.25rem acima da barra: o clip do scroll é a
 * borda do PADDING do corpo, mas o sticky não sobe além da borda de CONTEÚDO,
 * e sobrava uma fresta da altura do `padding-top` onde o conteúdo rolado
 * continuava visível.
 */
export function HotelDetailsTabs({
  tabs,
  activeId,
  copy,
  onActiveChange,
}: {
  readonly tabs: readonly HotelDetailsTab[];
  readonly activeId: string;
  readonly copy: HotelDetailsTabsCopy;
  readonly onActiveChange: (id: string) => void;
}) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  return (
    <nav
      className="genui2 sticky top-0 z-10 -mx-6 min-w-0 border-b border-border bg-card px-3 pointer-coarse:px-0 before:absolute before:inset-x-0 before:-top-1 before:h-1 before:bg-card before:content-['']"
      aria-label={copy.sectionsNavigationLabel}
    >
      <ArrowStrip
        scrollerSelector='[data-slot="hotel-details-tabs"]'
        previousLabel={copy.previousTabsLabel}
        nextLabel={copy.nextTabsLabel}
      >
        <div
          data-slot="hotel-details-tabs"
          className="flex min-h-9 min-w-0 items-stretch overflow-x-auto overflow-y-hidden px-3 pointer-coarse:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              type="button"
              aria-current={tab.id === activeId ? 'location' : undefined}
              /* A ativa ganha o sublinhado de 2px encostado na borda da barra
                 (`-bottom-px` cobre o `border-b` do shell) — o `after:` só
                 existe nela, pra as inativas não reservarem a altura. */
              className="text-ui relative shrink-0 cursor-pointer px-3 py-2.5 whitespace-nowrap text-muted-foreground outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--ring)] aria-[current=location]:text-foreground aria-[current=location]:after:absolute aria-[current=location]:after:inset-x-0 aria-[current=location]:after:-bottom-px aria-[current=location]:after:h-0.5 aria-[current=location]:after:bg-current aria-[current=location]:after:content-['']"
              onClick={() => {
                onActiveChange(tab.id);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                event.preventDefault();
                const offset = event.key === 'ArrowRight' ? 1 : -1;
                const nextIndex = (index + offset + tabs.length) % tabs.length;
                const next = tabs[nextIndex];
                if (!next) return;
                onActiveChange(next.id);
                tabRefs.current[nextIndex]?.focus();
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </ArrowStrip>
    </nav>
  );
}
